/* Partner Management → Product weights: grams per unit saved from Hub uploads (shared by all partners) — used to work
   out dropship parcel weight → shipping slab. Rules cover tablets/capsules, tubes, vials and jellies; this list is the rest. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Scale, Trash2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";

const Row = ({ x, onDone }) => {
  const [g, setG] = useState(String(x.grams));
  const save = async () => {
    try { await axios.put(`${API}/portal-admin/unit-weights`, { name: x.name, grams: Number(g) }, { headers: getAuthHeader() }); toast.success("Saved"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const del = async () => {
    if (!window.confirm(`Remove the saved weight for ${x.name}? The Hub will ask for it again.`)) return;
    await axios.delete(`${API}/portal-admin/unit-weights/${encodeURIComponent(x.key)}`, { headers: getAuthHeader() }); onDone();
  };
  return (
    <tr className="border-t" data-testid={`uw-row-${x.key}`}>
      <td className="px-3 py-2 font-medium">{x.name}</td>
      <td className="px-3 py-2"><input value={g} onChange={(e) => setG(e.target.value)} inputMode="decimal" className="w-20 rounded border px-2 py-1 text-xs" data-testid={`uw-grams-${x.key}`} /> g
        {String(x.grams) !== g && <button onClick={save} className="ml-2 rounded bg-indigo-600 px-2 py-1 text-[11px] font-semibold text-white" data-testid={`uw-save-${x.key}`}>Save</button>}</td>
      <td className="px-3 py-2 text-xs text-slate-500">{x.updated_by} · {String(x.updated_at || "").slice(0, 10)}</td>
      <td className="px-3 py-2"><button onClick={del} className="text-rose-600" data-testid={`uw-del-${x.key}`}><Trash2 className="h-4 w-4" /></button></td>
    </tr>);
};

export const UnitWeightsTab = () => {
  const [rows, setRows] = useState(null);
  const [nm, setNm] = useState(""); const [g, setG] = useState("");
  const load = () => axios.get(`${API}/portal-admin/unit-weights`, { headers: getAuthHeader() }).then((r) => setRows(r.data.items)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await axios.put(`${API}/portal-admin/unit-weights`, { name: nm, grams: Number(g) }, { headers: getAuthHeader() }); setNm(""); setG(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="space-y-3" data-testid="uw-tab">
      <p className="flex items-start gap-2 text-sm text-slate-600"><Scale className="mt-0.5 h-4 w-4 text-indigo-600" />
        Dropship shipping uses parcel weight: tablets/capsules 1 g each, ointment/cream/gel/drops size × 1.5, vials/ampoules mL × 5, jelly 5 g × 1.6. Other products get their grams per unit here (typed once in the Hub upload, shared by all partners).</p>
      <div className="flex flex-wrap gap-2">
        <input value={nm} onChange={(e) => setNm(e.target.value)} placeholder="Product name" className="h-9 w-72 rounded-lg border px-3 text-sm" data-testid="uw-new-name" />
        <input value={g} onChange={(e) => setG(e.target.value)} placeholder="Grams per unit" inputMode="decimal" className="h-9 w-36 rounded-lg border px-3 text-sm" data-testid="uw-new-grams" />
        <button onClick={add} disabled={!nm.trim() || !(Number(g) > 0)} className="h-9 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white disabled:opacity-50" data-testid="uw-add">Add</button>
      </div>
      {rows === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : !rows.length ? <p className="text-sm text-slate-400" data-testid="uw-empty">No saved weights yet.</p> : (
        <div className="overflow-x-auto rounded-lg border"><table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr><th className="px-3 py-2">Product</th><th className="px-3 py-2">Grams per unit</th><th className="px-3 py-2">Saved by</th><th /></tr></thead>
          <tbody>{rows.map((x) => <Row key={x.key} x={x} onDone={load} />)}</tbody></table></div>)}
    </div>
  );
};
